import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  GlobalOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import DocumentsContext from '@totem/components/documents/report/documentsContext';
import useDocumentAccess from '@totem/components/documents/report/useDocumentAccess';
import { DocumentSummaryEntry } from '@totem/components/documents/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { SecurityGroupCheckConstraints } from '@totem/types/user';
import { formatDate, isNotNull } from '@totem/utilities/common';
const FILTER_COLUMNS = ['refType', 'region', 'building', 'controlSystem'];

const DocumentsTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { canOpen, open } = useDocumentAccess();
  const { data, loading, totalRecords, onAction } =
    useContext(DocumentsContext);
  const { handleTableChange, input, getFilterOptions, getFilterValues } =
    useContext(InputContext);

  // Controlled so removing a filter chip also clears the column. Keyed on the selected values so the arrays only
  // change when a selection does; a new array every render makes antd fire onChange in a loop.
  const filterValuesKey = JSON.stringify(
    FILTER_COLUMNS.map((key) => getFilterValues(key)),
  );
  const filteredValues = useMemo(() => {
    const values: string[][] = JSON.parse(filterValuesKey);
    return Object.fromEntries(
      FILTER_COLUMNS.map((key, index) => [key, values[index]]),
    );
  }, [filterValuesKey]);
  const columns: ColumnProps<DocumentSummaryEntry>[] = [
    {
      title: '',
      dataIndex: 'document.id',
      key: 'download',
      render: (_, record: DocumentSummaryEntry) => {
        if (!canOpen(record)) {
          return <LockOutlined style={{ color: 'red' }} />;
        }
        if (record.document.fileType === 'Link') {
          return <GlobalOutlined onClick={() => open(record)} />;
        }
        return <CloudDownloadOutlined onClick={() => open(record)} />;
      },
    },
    {
      title: 'Name',
      dataIndex: 'document.displayName',
      key: 'name',
      render: (_, record: DocumentSummaryEntry) => {
        if (!canOpen(record)) {
          return <span>{record.document.displayName}</span>;
        }
        return (
          <button
            type="button"
            onClick={() => open(record)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
              font: 'inherit',
            }}
          >
            {record.document.displayName}
          </button>
        );
      },
      sorter: true,
    },
    {
      title: 'Version',
      dataIndex: 'document.version',
      key: 'version',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{record.document.version}</span>
      ),
      sorter: true,
    },
    {
      title: 'Size',
      dataIndex: 'document.size',
      key: 'size',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{record.document.size}</span>
      ),
      sorter: true,
    },
    {
      title: 'Ref Type',
      dataIndex: 'document.objectType',
      key: 'refType',
      filters: getFilterOptions('refType'),
      filteredValue: filteredValues.refType,
      filterSearch: true,
      render: (_, record: DocumentSummaryEntry) => (
        <span>{record.document.objectType}</span>
      ),
      sorter: true,
    },
    {
      title: 'Ref Name',
      dataIndex: 'document.objectDisplay',
      key: 'refName',
      render: (_, record: DocumentSummaryEntry) => {
        switch (record.document.objectType) {
          case 'Device':
            return (
              <Link to={`/dashboard/devices/${record.document.objectId}`}>
                {record.document.objectDisplay}
              </Link>
            );
          case 'Maintenance Window':
            return (
              <Link
                to={`/dashboard/maintenanceWindows/${record.document.objectId}`}
              >
                {record.document.objectDisplay}
              </Link>
            );
          default:
            return <span>{record.document.objectDisplay}</span>;
        }
      },
      sorter: true,
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'region',
      filters: getFilterOptions('region'),
      filteredValue: filteredValues.region,
      filterSearch: true,
      render: (_, record: DocumentSummaryEntry) => {
        return <span>{record.region.name}</span>;
      },
      sorter: true,
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      filters: getFilterOptions('building'),
      filteredValue: filteredValues.building,
      filterSearch: true,
      render: (_, record: DocumentSummaryEntry) => {
        if (record.building.id !== '') {
          return (
            <Link to={`/dashboard/buildings/${record.building.id}`}>
              {record.building.name}
            </Link>
          );
        }
        return <span>{record.building.name}</span>;
      },
      sorter: true,
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystem',
      filters: getFilterOptions('controlSystem'),
      filteredValue: filteredValues.controlSystem,
      filterSearch: true,
      render: (_, record: DocumentSummaryEntry) => {
        if (record.controlSystem.id !== '') {
          return (
            <Link to={`/dashboard/controlsystems/${record.controlSystem.id}`}>
              {record.controlSystem.name}
            </Link>
          );
        }
        return <span>{record.controlSystem.name}</span>;
      },
      sorter: true,
    },
    {
      title: 'Created By',
      dataIndex: 'document.createdBy',
      key: 'createdBy',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{record.document.createdBy}</span>
      ),
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'document.createdAt',
      key: 'createdAt',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{formatDate(record.document.createdAt)}</span>
      ),
      sorter: true,
    },
    {
      title: 'Updated At',
      dataIndex: 'document.updatedAt',
      key: 'updatedAt',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{formatDate(record.document.updatedAt)}</span>
      ),
      sorter: true,
    },
    {
      title: 'Expires At',
      dataIndex: 'document.expirationDate',
      key: 'expirationDate',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{formatDate(record.document.expirationDate)}</span>
      ),
      sorter: true,
    },
    {
      title: 'Actions',
      dataIndex: 'document.id',
      key: 'actions',
      render: (_, record: DocumentSummaryEntry) => {
        const securityContext: SecurityGroupCheckConstraints = {
          regionId: record.region.id,
          buildingId: record.building.id,
          controlSystemId: record.controlSystem.id,
        };
        if (
          record.document.createdBy === userProfile.email ||
          permissions.documentsAdministrator.hasPermissions(
            userProfile,
            securityContext,
          )
        ) {
          return (
            <IBTooltip
              content={
                <div>
                  {permissions.documentsAdministrator.tooltip}
                  <br />
                  OR
                  <br />
                  Document Uploader
                </div>
              }
            >
              <IBDIcon
                name={'Edit'}
                title={`Delete ${record.document.displayName}`}
                onClick={() => onAction('edit', record)}
                style={{ marginRight: '8px' }}
              />
              <DeleteOutlined
                title={`Delete ${record.document.displayName}`}
                onClick={() => onAction('hard-delete', record)}
              />
            </IBTooltip>
          );
        }
        return <span />;
      },
    },
  ];
  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(data) ? data : []}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.document.id}
    />
  );
};
export default DocumentsTable;
